import React from 'react';
import { FiX, FiUser, FiHome, FiCalendar, FiCheckCircle, FiXCircle, FiClock, FiFileText } from 'react-icons/fi';

interface Claim {
  id: string;
  userId: string;
  companyId: string;
  isApproved: boolean;
  isActive: boolean;
  notes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  company?: {
    slug: string;
    nameTh: string;
    nameEn?: string;
  };
}

interface ViewClaimDetailsModalProps {
  claim: Claim | null;
  isOpen: boolean;
  onClose: () => void;
}

const ViewClaimDetailsModal: React.FC<ViewClaimDetailsModalProps> = ({
  claim,
  isOpen,
  onClose
}) => {
  console.log('📋 ViewClaimDetailsModal render - isOpen:', isOpen, 'claim:', claim ? 'exists' : 'null');

  if (!isOpen || !claim) {
    console.log('❌ Modal blocked - isOpen:', isOpen, 'claim:', claim ? 'exists' : 'null');
    return null;
  }

  console.log('✅ Modal will render!');

  // Helper function to get status from isApproved and isActive
  const getClaimStatus = (claim: Claim): 'pending' | 'approved' | 'rejected' => {
    if (claim.isApproved && claim.isActive) {
      return 'approved';
    } else if (!claim.isApproved && claim.isActive === false) {
      return 'rejected';
    }
    return 'pending';
  };

  const status = getClaimStatus(claim);

  const statusConfig = {
    pending: {
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: <FiClock className="h-5 w-5 mr-2" />,
      label: 'รอดำเนินการ',
      bgColor: 'bg-yellow-50'
    },
    approved: {
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: <FiCheckCircle className="h-5 w-5 mr-2" />,
      label: 'อนุมัติแล้ว',
      bgColor: 'bg-green-50'
    },
    rejected: {
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: <FiXCircle className="h-5 w-5 mr-2" />,
      label: 'ปฏิเสธแล้ว',
      bgColor: 'bg-red-50'
    }
  };

  const currentStatus = statusConfig[status];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <FiFileText className="h-6 w-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">
                รายละเอียดคำขอสิทธิ์
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            <div className={`flex items-center justify-center p-4 rounded-lg border ${currentStatus.color}`}>
              {currentStatus.icon}
              <span className="text-lg font-semibold">{currentStatus.label}</span>
            </div>

            {/* Company Information */}
            <div className="bg-blue-50 rounded-lg p-5 border border-blue-100">
              <div className="flex items-start">
                <FiHome className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-blue-900 mb-3">
                    ข้อมูลบริษัท
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-blue-700 font-medium">ชื่อบริษัท (ไทย)</label>
                      <p className="text-base font-semibold text-blue-900 mt-1">
                        {claim.company?.nameTh || 'ไม่ระบุชื่อบริษัท'}
                      </p>
                    </div>
                    {claim.company?.nameEn && (
                      <div>
                        <label className="text-xs text-blue-700 font-medium">ชื่อบริษัท (English)</label>
                        <p className="text-sm text-blue-800 mt-1">{claim.company.nameEn}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-blue-700 font-medium">Slug</label>
                      <p className="text-sm text-blue-800 mt-1 font-mono">{claim.company?.slug || '-'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-blue-700 font-medium">Company ID</label>
                      <p className="text-sm text-blue-800 mt-1 font-mono">{claim.companyId}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Information */}
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <div className="flex items-start">
                <FiUser className="h-6 w-6 text-gray-600 mr-3 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    ข้อมูลผู้ขอสิทธิ์
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-600 font-medium">ชื่อ-นามสกุล</label>
                      <p className="text-sm font-semibold text-gray-900 mt-1">
                        {claim.user?.firstName && claim.user?.lastName
                          ? `${claim.user.firstName} ${claim.user.lastName}`
                          : 'ไม่ระบุชื่อ'}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">อีเมล</label>
                      <p className="text-sm text-gray-900 mt-1">{claim.user?.email || 'ไม่มีอีเมล'}</p>
                    </div>
                    {claim.user?.phone && (
                      <div>
                        <label className="text-xs text-gray-600 font-medium">เบอร์โทร</label>
                        <p className="text-sm text-gray-900 mt-1">{claim.user.phone}</p>
                      </div>
                    )}
                    <div>
                      <label className="text-xs text-gray-600 font-medium">User ID</label>
                      <p className="text-sm text-gray-900 mt-1 font-mono">{claim.userId}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Claim Details */}
            <div className="bg-white rounded-lg p-5 border border-gray-200">
              <div className="flex items-start">
                <FiCalendar className="h-6 w-6 text-gray-600 mr-3 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    รายละเอียดคำขอ
                  </h3>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-600 font-medium">Claim ID</label>
                      <p className="text-sm text-gray-900 mt-1 font-mono">{claim.id}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">วันที่สร้างคำขอ</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {new Date(claim.createdAt).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 font-medium">วันที่อัปเดทล่าสุด</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {new Date(claim.updatedAt).toLocaleDateString('th-TH', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div>
                        <label className="text-xs text-gray-600 font-medium">สถานะการอนุมัติ</label>
                        <p className="text-sm text-gray-900 mt-1">
                          {claim.isApproved ? (
                            <span className="text-green-600 font-semibold">อนุมัติแล้ว</span>
                          ) : (
                            <span className="text-gray-500">ยังไม่อนุมัติ</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 font-medium">สถานะการใช้งาน</label>
                        <p className="text-sm text-gray-900 mt-1">
                          {claim.isActive ? (
                            <span className="text-green-600 font-semibold">ใช้งานอยู่</span>
                          ) : (
                            <span className="text-red-600 font-semibold">ไม่ใช้งาน</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* User Notes */}
            {claim.notes && (
              <div className="bg-blue-50 rounded-lg p-5 border border-blue-100">
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  หมายเหตุจากผู้ขอสิทธิ์
                </h3>
                <p className="text-sm text-blue-800 whitespace-pre-wrap">{claim.notes}</p>
              </div>
            )}

            {/* Rejection Reason */}
            {status === 'rejected' && claim.rejectionReason && (
              <div className="bg-red-50 rounded-lg p-5 border border-red-200">
                <h3 className="text-sm font-medium text-red-900 mb-2">
                  เหตุผลในการปฏิเสธ
                </h3>
                <p className="text-sm text-red-800 whitespace-pre-wrap">{claim.rejectionReason}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
            >
              ปิด
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewClaimDetailsModal;
